import { NumberFormatter as NF } from "../../../utils/NumberFormatter";
import { MathUtils } from "../../../utils/MathUtils";
import { type LinearCstic, type LinearSubstep } from "../models";
import { type WithTranslation } from "react-i18next";
import { getCsticNumberVal } from "../utils";
import * as RailCstics from "../../Rail/v2/configuration/classic/RailsCstics";
import { type ReactNode } from "react";
import { getExtraValidationParamName } from "~/Configurators/Linear/namingUtils";
import { useConfiguratorType } from "~/Configurators/common/useConfiguratorType";
import LanguageAwareExternalContent from "~/Configurators/Linear/components/LanguageAwareExternalContent";
import { ConfiguratorType } from "~/Configurators/ConfiguratorType";
import { KmatType } from "~/Configurators/Linear/kmatType";

export function makeMinMaxLabel(t: WithTranslation["t"], min: number | null, max: number | null) {
  return t("page.linear.minmax", { minVal: formatValue(min), maxVal: formatValue(max) });
}

const formatValue = (num: number | null) => {
  return num === null
    ? "MISSING"
    : NF.getDefaultFormatter().format(
        MathUtils.round(num, {
          fractionalDigits: 2,
          mode: "ceil",
        }),
      );
};

const makeSingleAlarProcessor = ({
  def,
  cstics,
  kmatIndex,
  t,
}: {
  def: LinearSubstep;
  cstics: Dict<LinearCstic>;
  t: WithTranslation["t"];
  kmatIndex: number;
}) => {
  // Used in system configurator
  const alarHelperLabelFactory = makeAlarHelperLabelFactory({
    minRecommended: getCsticNumberVal(cstics[RailCstics.AlarMinRecommended]),
    maxRecommended: getCsticNumberVal(cstics[RailCstics.AlarMaxRecommended]),
    hardMax: getCsticNumberVal(cstics[RailCstics.AlarHardMax]),
    t,
  });
  return (csticName: string) => {
    const cstic = cstics[csticName];

    const isValid =
      def.additionalValidations[
        getExtraValidationParamName({
          index: kmatIndex,
          key: csticName,
          type: KmatType.SubRail,
        })
      ] ?? (true as boolean);

    const value = getCsticNumberVal(cstic);

    const { label: helperLabel, isNotRecommended } = alarHelperLabelFactory({ isValid, value });

    return {
      value,
      cstic,
      isValid,
      helperLabel,
      isNotRecommended,
    };
  };
};

export const useALARLogic = (props: {
  def: LinearSubstep;
  cstics: Dict<LinearCstic>;
  t: WithTranslation["t"];
  kmatIndex: number;
}) => {
  // Used in system configurator
  const processCstic = makeSingleAlarProcessor(props);

  return {
    aL: processCstic(RailCstics.AL),
    aR: processCstic(RailCstics.AR),
  };
};

export const makeAlarHelperLabelFactory =
  ({
    minRecommended,
    maxRecommended,
    hardMax,
    t,
  }: {
    minRecommended: number | null;
    maxRecommended: number | null;
    hardMax: number | null;
    t: WithTranslation["t"];
  }) =>
  ({ isValid, value }: { isValid: boolean; value: number | null }): { label: ReactNode; isNotRecommended: boolean } => {
    const configuratorType = useConfiguratorType();
    let recommendedLabel;
    if (configuratorType === ConfiguratorType.Rail) {
      // TODO: This should be sourced from elsewhere maybe?
      recommendedLabel = <LanguageAwareExternalContent content={"configurator.rail.section.alar.recommended"} />;
    } else {
      recommendedLabel = <LanguageAwareExternalContent content={"configurator.unit.section.alar.recommended"} />;
    }
    if (value === null) {
      return {
        label: undefined,
        isNotRecommended: false,
      };
    }
    if (!isValid) {
      if (value <= 0) {
        return {
          label: <span>&gt;0mm</span>,
          isNotRecommended: false,
        };
      }
      if (hardMax && value > hardMax) {
        return {
          label: <span>&lt;={hardMax}mm</span>,
          isNotRecommended: false,
        };
      }
    }
    if (minRecommended && maxRecommended && (minRecommended >= value || value >= maxRecommended)) {
      return {
        label: (
          <>
            {recommendedLabel}
            <span>{makeMinMaxLabel(t, minRecommended, maxRecommended)}</span>
          </>
        ),
        isNotRecommended: true,
      };
    }
    return {
      label: undefined,
      isNotRecommended: false,
    };
  };
